package com.evoapps.evoshift.data.remote

import com.evoapps.evoshift.data.session.LocalCache
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.Transaction
import dev.gitlive.firebase.firestore.WriteBatch
import dev.gitlive.firebase.firestore.firestore
import kotlin.concurrent.Volatile
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.withTimeoutOrNull

/**
 * The app's Firestore (SH-177). The repositories hold this handle, not the SDK's `FirebaseFirestore`:
 * signing out [clear]s the offline cache, which the SDK allows only on a terminated instance, and a
 * terminated instance is dead for good ("the client has already been terminated"). The handle lets
 * the old instance go and gives the next call a fresh one, so the repositories, bound once in the
 * DI graph, keep working through sign-out and the next sign-in.
 *
 * A fresh instance is what the SDK returns from `Firebase.firestore` after `terminate()` on every
 * target: the Android and Java SDKs drop the terminated instance from their registry, the iOS SDK
 * from the app's, the web SDK from its component provider. What the first instance was configured
 * with (an emulator host in local development) is applied again through [configure].
 *
 * So repositories call [collection], [document] and [batch] per use and never keep a
 * `CollectionReference`, `DocumentReference` or `Query` in a property: those hold the instance they
 * were made from, and after a sign-out that instance answers "the client has already been
 * terminated" (`FirestoreReferencesTest` checks the sources).
 *
 * The live instance is one per process, like the SDK's own: every handle (a test harness builds a
 * second DI graph beside the app's) shares it, and a sign-out through any of them replaces it for
 * all. [configure] runs once per live instance, not once per handle: on the web, applying settings
 * a second time while the instance is alive is `initializeFirestore()` with a new settings object,
 * which the SDK refuses ("already been called with different options").
 */
class AppFirestore(private val create: () -> FirebaseFirestore = { Firebase.firestore }) : LocalCache {

    /** The SDK's instance, started on first use. */
    val instance: FirebaseFirestore
        get() = shared ?: start()

    private fun start(): FirebaseFirestore = create().also {
        if (!configured) {
            configure(it)
            configured = true
        }
        shared = it
    }

    fun collection(collectionPath: String): CollectionReference = instance.collection(collectionPath)

    fun collectionGroup(collectionId: String): Query = instance.collectionGroup(collectionId)

    fun document(documentPath: String): DocumentReference = instance.document(documentPath)

    fun batch(): WriteBatch = instance.batch()

    suspend fun <T> runTransaction(func: suspend Transaction.() -> T): T = instance.runTransaction(func)

    /**
     * Writes still on the device get [PENDING_WRITES_WAIT] to reach the server. Before the auth
     * sign-out: the SDK fails the wait as soon as the user changes. Offline, the wait runs out and
     * [clear] drops them, as a reinstall would.
     */
    override suspend fun flush() {
        val started = shared ?: return
        withTimeoutOrNull(PENDING_WRITES_WAIT) { started.waitForPendingWrites() }
    }

    /**
     * Terminates the instance and clears its offline cache, then [clearPlatformCache]. An instance
     * not started in this process is created for it: the files on disk may still be a previous
     * launch's. Each step is tried even when the one before failed, and the next call to this
     * handle starts a fresh instance either way.
     */
    override suspend fun clear() {
        val old = shared ?: start()
        try {
            attempt("terminate") { old.terminate() }
            attempt("clearPersistence") { old.clearPersistence() }
            attempt("clearPlatformCache") { clearPlatformCache() }
        } finally {
            // The next call, through any handle, starts and configures a fresh instance.
            shared = null
            configured = false
        }
    }

    private suspend fun attempt(step: String, block: suspend () -> Unit) {
        try {
            block()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("Firestore cache: $step failed: ${e.message}")
        }
    }

    companion object {
        val PENDING_WRITES_WAIT: Duration = 3.seconds

        @Volatile
        private var shared: FirebaseFirestore? = null

        /** Whether [configure] ran for [shared]; false again after a [clear]. */
        @Volatile
        private var configured = false

        /**
         * Applied to every instance when it is created, the first and each one after a [clear]:
         * the emulator host and settings in local development and tests (the desktop app, the web
         * app's `?emulator`, the Android UI tests' runner). Production needs nothing.
         */
        var configure: (FirebaseFirestore) -> Unit = {}

        /** [configure]s every instance for the Firestore emulator at [host]:[port]. */
        fun useEmulator(host: String, port: Int) {
            configure = { it.useEmulator(host, port) }
        }

        /**
         * What the platform keeps of the cache besides what `clearPersistence` removes, deleted
         * while the instance is terminated: the desktop app's SQLite files (DesktopFirebase).
         */
        var clearPlatformCache: suspend () -> Unit = {}
    }
}
